import { useAuth0 } from '@auth0/auth0-react';
import { Login } from 'features/auth/types';
import { fetchLogin } from '../utils/federatedApi';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNotification } from 'features/notifications';
import { useContext } from 'react';
import { AuthTokenContext } from './authContext';
import { clearTableStateStorage } from 'features/misc/table/shared/utils/clearTableStateOnFullPageLoad';

const userKey = ['federated-current-user'];

export const useAuthState = () => useContext(AuthTokenContext);

export const useUser = () => {
  const { isAuthenticated, isLoading, token } = useAuthState();
  const enabled = !isLoading && isAuthenticated;
  return useQuery<Login, Error>({
    queryKey: userKey,
    queryFn: async () => await fetchLogin(token ?? ''),
    enabled,
  });
};

export const useLogout = (): (() => Promise<void>) => {
  const { logout } = useAuth0();
  const queryClient = useQueryClient();
  const { dismissNotification } = useNotification();

  return async () => {
    queryClient.invalidateQueries({ queryKey: userKey });
    dismissNotification();
    clearTableStateStorage();
    await logout();
  };
};

export const useLogin = (): (() => Promise<void>) => {
  const { loginWithRedirect, error } = useAuth0();
  const queryClient = useQueryClient();
  const { notify, dismissNotification } = useNotification();

  return async () => {
    dismissNotification();
    if (error) notify.error({ id: 'error', message: error?.message });
    queryClient.invalidateQueries({ queryKey: userKey });
    await loginWithRedirect();
  };
};
